/**
 * Cartão de escolha de jogo.
 *
 * Vem do `DestinationCard`, e mantém dele o que é visual: imagem ao
 * fundo com zoom no hover, véu de gradiente na cor do tema, brilho
 * colorido em volta, e a barra de ação embaixo. Três coisas mudaram, e
 * as três por motivo, não por gosto:
 *
 * **Continua sendo `<button role="radio">`, não `<a href>`.** Isto não
 * navega para lugar nenhum: escolhe qual jogo entra na fila, e a escolha
 * é lida depois junto com o valor da entrada. Trocar por âncora tiraria
 * a navegação por setas do teclado, tiraria o estado marcado do leitor
 * de tela, e faria o cartão prometer uma página que não existe.
 *
 * **O fundo é desenhado, não fotografado.** Não temos foto de xadrez,
 * damas ou jogo da velha, e puxar imagem de banco de terceiro colocaria
 * o visual da tela na dependência de um servidor que não é nosso — além
 * de entregar a esse servidor o endereço IP de cada jogador que abrisse
 * o lobby. O tabuleiro sai de gradiente CSS: nada para baixar, nada que
 * quebre offline, e o desenho acompanha o jogo.
 *
 * **A cor é OKLCH, não HSL.** É o espaço que o resto do sistema usa. Em
 * OKLCH, mesma lightness significa mesmo peso visual aos olhos, então as
 * três cores de tema ficam equilibradas entre si sem ajuste manual — em
 * HSL não ficariam.
 */
import { ArrowRight } from 'lucide-react';
import type { ReactNode } from 'react';

import type { GameType } from '../lib/adapter.ts';

/**
 * Cor e tabuleiro de cada jogo.
 *
 * `cor` é escura de propósito nos três: o texto do cartão é
 * branco e precisa passar em contraste sobre ela nos dois temas, claro
 * e escuro. Cartão que fica ilegível quando o sistema vira para claro é
 * o defeito mais comum deste tipo de arte.
 */
interface TemaDoJogo {
  readonly cor: string;
  readonly marca: string;
  readonly padrao: 'XADREZ' | 'GRADE';
}

/**
 * `NEUTRO` é a mesa sem jogo escolhido, usada na prévia do perfil: ali o
 * assunto é a pessoa, não a partida, e pintar de xadrez afirmaria um
 * jogo que ninguém escolheu.
 */
export type TemaDeMesa = GameType | 'NEUTRO';

const TEMA: Record<TemaDeMesa, TemaDoJogo> = {
  CHESS: { cor: 'oklch(0.34 0.075 265)', marca: '♞', padrao: 'XADREZ' },
  CHECKERS: { cor: 'oklch(0.36 0.095 30)', marca: '⛃', padrao: 'XADREZ' },
  TIC_TAC_TOE: { cor: 'oklch(0.36 0.070 175)', marca: '✕', padrao: 'GRADE' },
  NEUTRO: { cor: 'oklch(0.32 0.055 155)', marca: '♟', padrao: 'XADREZ' },
};

/**
 * O tabuleiro ao fundo.
 *
 * Casas alternadas para xadrez e damas; três linhas cruzadas para o jogo
 * da velha. Claras o bastante para dar textura, apagadas o bastante para
 * o texto por cima continuar sendo o assunto.
 */
function fundoDoTabuleiro(padrao: 'XADREZ' | 'GRADE'): string {
  if (padrao === 'XADREZ') {
    return 'repeating-conic-gradient(rgb(255 255 255 / 0.09) 0% 25%, transparent 0% 50%)';
  }
  return [
    'linear-gradient(to right, transparent 32%, rgb(255 255 255 / 0.13) 32% 34%, transparent 34% 65%, rgb(255 255 255 / 0.13) 65% 67%, transparent 67%)',
    'linear-gradient(to bottom, transparent 32%, rgb(255 255 255 / 0.13) 32% 34%, transparent 34% 65%, rgb(255 255 255 / 0.13) 65% 67%, transparent 67%)',
  ].join(', ');
}

/**
 * As camadas de fundo do cartão: tabuleiro, peça e véu.
 *
 * Separado do `MesaCard` porque o salão precisa da mesma moldura em
 * volta de conteúdo diferente — mesa aberta com avatar do anfitrião,
 * mesa em jogo com os dois. Copiar o desenho em três lugares faria as
 * três versões divergirem no primeiro ajuste.
 *
 * Quem usa isto precisa pôr `--tema` no elemento de fora, com
 * `corDoJogo`, e o conteúdo por cima precisa de `relative`.
 */
export function CamadasDeMesa({ gameType }: { gameType: TemaDeMesa }) {
  const tema: TemaDoJogo = TEMA[gameType];
  return (
    <>
      {/* Tabuleiro, com zoom no hover. `aria-hidden` porque é textura:
          anunciá-lo só atrapalharia quem ouve a tela. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 transition-transform duration-500 ease-out group-hover:scale-110"
        style={{
          backgroundColor: 'var(--tema)',
          backgroundImage: fundoDoTabuleiro(tema.padrao),
          backgroundSize: tema.padrao === 'XADREZ' ? '3rem 3rem' : '100% 100%',
        }}
      />

      {/* A peça, grande e apagada. Decoração — o nome do jogo está no
          texto, e repetir em símbolo confundiria leitor de tela sem
          acrescentar nada. */}
      <span
        aria-hidden="true"
        className="absolute -top-2 right-3 leading-none select-none transition-transform duration-500 ease-out group-hover:-translate-y-1 group-hover:scale-110"
        style={{ fontSize: '7rem', color: 'rgb(255 255 255 / 0.14)' }}
      >
        {tema.marca}
      </span>

      {/* Véu: escurece de baixo para cima, para o texto ter onde pousar. */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(to top, oklch(0.16 0.02 150 / 0.92), oklch(0.16 0.02 150 / 0.55) 45%, transparent 78%)',
        }}
      />
    </>
  );
}

/** A cor do jogo, para pôr em `--tema` no elemento de fora. */
export function corDoJogo(gameType: TemaDeMesa): string {
  return TEMA[gameType].cor;
}

/**
 * Classes da moldura.
 *
 * `interativo` liga hover e brilho; cartão que não faz nada ao clique
 * não deve crescer sob o cursor, porque isso é promessa de ação.
 */
export function molduraDeMesa(estado: {
  selecionado?: boolean;
  interativo?: boolean;
}): string {
  const { selecionado = false, interativo = true } = estado;
  return [
    'group relative block w-full overflow-hidden text-left rounded-2xl',
    'transition-[transform,box-shadow] duration-500 ease-out',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)] focus-visible:ring-offset-2',
    'disabled:opacity-60 disabled:pointer-events-none',
    // O brilho na cor do jogo é o que dá relevo ao cartão. Mais forte no
    // escolhido, e o aro na cor do texto da página com folga: branco
    // funcionava no tema escuro e sumia no claro, porque ali o fundo
    // atrás do cartão também é claro.
    selecionado
      ? 'scale-[1.02] shadow-[0_0_60px_-12px_var(--tema)] ring-2 ring-[var(--foreground)] ring-offset-2 ring-offset-[var(--background)]'
      : 'shadow-[0_0_28px_-18px_var(--tema)]',
    interativo && !selecionado
      ? 'hover:scale-[1.03] hover:shadow-[0_0_60px_-15px_var(--tema)]'
      : '',
  ]
    .filter((c) => c !== '')
    .join(' ');
}

/**
 * A barra de baixo: rótulo à esquerda, seta à direita.
 *
 * `.rotulo` é o cinza desenhado para o cartão claro, e não passa em
 * contraste sobre este fundo escuro. Sobrescrito aqui, e só aqui, para
 * não mexer no componente que outras telas usam sobre fundo claro.
 */
export function BarraDeMesa({
  children,
  comSeta = true,
}: {
  children: ReactNode;
  comSeta?: boolean;
}) {
  return (
    <div className="mt-4 flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 backdrop-blur-md transition-colors duration-300 bg-white/10 border-white/25 group-hover:bg-white/20 group-hover:border-white/40 [&_.rotulo]:text-white/85">
      {/* `flex-1` para o rótulo ocupar a largura: sem ele, conteúdo que
          quer empurrar um valor para a direita não tem espaço a dividir. */}
      <span className="text-sm font-semibold tracking-wide min-w-0 flex-1">{children}</span>
      {comSeta ? (
        <ArrowRight
          aria-hidden="true"
          className="h-4 w-4 shrink-0 transition-transform duration-300 group-hover:translate-x-1"
        />
      ) : null}
    </div>
  );
}

/**
 * Selo de estado.
 *
 * `flutuante` o prende ao canto de cima, o que só funciona quando o topo
 * do cartão está vazio — no cartão de escolha está, no do salão não: ali
 * ele cobria o avatar de quem abriu a mesa. Por padrão fica no fluxo,
 * que é o que não colide com nada.
 */
export function SeloDeMesa({
  children,
  flutuante = false,
}: {
  children: ReactNode;
  flutuante?: boolean;
}) {
  return (
    <span
      className={[
        'rounded-full bg-white px-2.5 py-1 text-[0.68rem] font-bold uppercase tracking-wider',
        flutuante ? 'absolute left-4 top-4' : 'self-start',
      ].join(' ')}
      style={{ color: 'var(--tema)' }}
    >
      {children}
    </span>
  );
}

export function MesaCard({
  gameType,
  titulo,
  descricao,
  selecionado,
  desabilitado = false,
  rodape,
  onSelecionar,
}: {
  gameType: GameType;
  titulo: string;
  descricao: string;
  selecionado: boolean;
  desabilitado?: boolean;
  /** Presença, ou o que mais a tela quiser mostrar na barra de baixo. */
  rodape?: ReactNode;
  onSelecionar: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selecionado}
      aria-label={titulo}
      disabled={desabilitado}
      onClick={onSelecionar}
      style={{ ['--tema' as string]: corDoJogo(gameType) }}
      className={`${molduraDeMesa({ selecionado })} min-h-[15rem] sm:min-h-[19rem]`}
    >
      <CamadasDeMesa gameType={gameType} />

      {/* O selo existe porque aro e cor sozinhos sao mudos para quem nao
          distingue as tres cores dos jogos. */}
      {selecionado ? <SeloDeMesa flutuante>Escolhida</SeloDeMesa> : null}

      <div className="relative flex h-full flex-col justify-end gap-1 p-5 text-white min-h-[15rem] sm:min-h-[19rem]">
        <h3 className="font-bold tracking-tight" style={{ fontSize: 'var(--text-title)' }}>
          {titulo}
        </h3>
        <p className="text-sm" style={{ color: 'rgb(255 255 255 / 0.82)' }}>
          {descricao}
        </p>

        <BarraDeMesa>{rodape ?? (selecionado ? 'Mesa escolhida' : 'Escolher')}</BarraDeMesa>
      </div>
    </button>
  );
}
